import { HashIcon, LinkIcon, RefreshCwIcon, TruckIcon } from 'lucide-react';
import { useParams } from 'react-router';

import type { CarrierDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import { DetailGroup } from '@/components/detail-group';
import { DetailList } from '@/components/detail-list';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { useEntityDetailsQuery } from '@/shared/hooks/queries';

import { CarrierServicesTable } from './carrier-services-table';
import { SurchargesCard } from './surcharges-card';
import { useCarrierSurcharges } from './use-carrier-surcharges';

export function CarrierDetails() {
  const { id } = useParams<{ id: string }>();
  const { shippingGateway } = useGateways();

  const { surcharges, isLoadingSurcharges, hasSurchargesError, createSurcharge, deleteSurcharge } =
    useCarrierSurcharges(id);

  const {
    data: carrier,
    isLoading,
    refetch,
  } = useEntityDetailsQuery<CarrierDTO>({
    parentKey: 'carriers',
    id,
    isNew: !id,
    query: async (carrierId, { signal }) => {
      const carriers = await shippingGateway.getCarriers(
        { include: { carrierServices: true } },
        { signal },
      );

      const matchedCarrier = carriers.items.find((item) => item.id === carrierId);

      if (!matchedCarrier) {
        throw new Error('Carrier not found');
      }

      return matchedCarrier;
    },
    retry: false,
  });

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/shipping/carriers" parentTitle="Carriers">
          <TruckIcon />
        </HeaderIcon>
        <HeaderTitle>{carrier?.name}</HeaderTitle>
        <HeaderActions>
          <Button
            size="icon"
            variant="outline"
            aria-label="Refresh"
            onClick={() => void refetch()}
            disabled={isLoading}
          >
            <RefreshCwIcon />
          </Button>
        </HeaderActions>
      </Header>
      <PageContent variant="split">
        <DetailMain>
          <CarrierServicesTable carrier={carrier} isLoading={isLoading} />
          <SurchargesCard
            carrier={carrier}
            surcharges={surcharges}
            isLoading={isLoading || isLoadingSurcharges}
            hasError={hasSurchargesError}
            createSurcharge={createSurcharge}
            deleteSurcharge={deleteSurcharge}
          />
        </DetailMain>
        <DetailSide>
          <DetailGroup constrainLabels>
            <DetailList
              title="Details"
              isLoading={isLoading}
              items={[
                {
                  icon: HashIcon,
                  label: 'Code',
                  value: carrier?.code,
                },
                {
                  icon: LinkIcon,
                  label: 'Base tracking url',
                  value: <span className="font-mono text-xs">{carrier?.baseTrackingUrl}</span>,
                },
              ]}
            />
          </DetailGroup>
        </DetailSide>
      </PageContent>
    </Page>
  );
}
